import { Box, Button, Flex, HStack, Icon, Stack, Text } from "@chakra-ui/react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FiChevronDown, FiChevronRight, FiShield } from "react-icons/fi";
import type { BuyerPublicBidDeclarationDto, BuyerPublicBidDeclarationsDto } from "../../../../autogen/bff-api";
import { presentationForTypeCode } from "../../../sourcing-events/portal/bid/espd/espdPresentationText";
import { SectionCard } from "../../../sourcing-events/portal/view-single/SectionCard";
import {
  EspdEmptyState,
  MutedValue,
  RequirementGroup,
  StatusPill,
  localeFor,
  localizedText,
  safeId,
} from "./espdResponseRendering";
import { useDefaultedExpansion } from "./useDefaultedExpansion";

// Takes the declarations tree directly (not the bid detail) so both the buyer bid-detail view and
// the supplier read-only bid page can render it from their own DTO. resetKey identifies the source
// bid so expansion state resets when a different bid's declarations are shown.
interface Props {
  declarations: BuyerPublicBidDeclarationsDto | null | undefined;
  resetKey: string;
}

export const DeclarationsTab = ({ declarations, resetKey }: Props) => {
  const { t } = useTranslation();
  const declarationItems = useMemo(() => declarations?.declarations ?? [], [declarations?.declarations]);
  const defaultExpanded = useMemo(
    () => declarationItems.filter((declaration) => declaration.answer === true).map((declaration) => declaration.id),
    [declarationItems],
  );
  const declarationIds = useMemo(() => declarationItems.map((declaration) => declaration.id), [declarationItems]);
  const { expanded, toggle } = useDefaultedExpansion({
    ids: declarationIds,
    defaultExpanded,
    resetKey,
  });

  if (!declarations?.hasResponse) {
    return <EspdEmptyState />;
  }

  return (
    <Stack spacing="5">
      <SectionCard icon={FiShield} title={t("Self-declaration – Part II")}>
        <Text color="smMuted" fontSize="sm" maxW="72ch">
          {t("Self-declarations from the submitted ESPD response.")}
        </Text>
      </SectionCard>

      {declarations.declarations.length > 0 && (
        <>
          <Flex align={{ base: "flex-start", md: "center" }} justify="space-between" gap="3" flexWrap="wrap">
            <Box>
              <Text fontWeight="bold" color="smText">
                {t("Detailed responses")}
              </Text>
              <Text fontSize="sm" color="smMuted">
                {t("Expand a question to inspect the submitted answers.")}
              </Text>
            </Box>
            <Text fontSize="sm" color="smMuted">
              {t("Read-only")}
            </Text>
          </Flex>

          <Box bg="smEspdCategoryBg" border="1px solid" borderColor="smBorder" borderRadius="md" p="3">
            <Stack spacing="2">
              {declarations.declarations.map((declaration) => (
                <DeclarationSection
                  key={declaration.id}
                  declaration={declaration}
                  isOpen={expanded.includes(declaration.id)}
                  onToggle={() => toggle(declaration.id)}
                />
              ))}
            </Stack>
          </Box>
        </>
      )}
    </Stack>
  );
};

const DeclarationSection = ({
  declaration,
  isOpen,
  onToggle,
}: {
  declaration: BuyerPublicBidDeclarationDto;
  isOpen: boolean;
  onToggle: () => void;
}) => {
  const { t, i18n } = useTranslation();
  const question = localizedText(
    i18n.language,
    declaration.questionSource,
    declaration.questionTranslation,
    declaration.question,
  );
  const curated = presentationForTypeCode(declaration.typeCode)?.titleText;
  const heading = curated
    ? localeFor(i18n.language).startsWith("nb")
      ? curated.no
      : curated.en
    : question;
  const buttonId = `declaration-button-${safeId(declaration.id)}`;
  const panelId = `declaration-panel-${safeId(declaration.id)}`;

  return (
    <Box border="1px solid" borderColor="smBorder" borderRadius="md" overflow="hidden" bg="smEspdCriterionBg">
      <Button
        id={buttonId}
        type="button"
        variant="unstyled"
        h="auto"
        w="full"
        display="block"
        textAlign="left"
        whiteSpace="normal"
        aria-expanded={isOpen}
        aria-controls={panelId}
        onClick={onToggle}
        _focusVisible={{ boxShadow: "smFocusRing", zIndex: 1 }}
      >
        <Box
          data-testid="declaration-row-grid"
          display="grid"
          gridTemplateColumns={{ base: "1fr", md: "minmax(0, 1fr) max-content" }}
          alignItems={{ base: "stretch", sm: "center" }}
          gap="3"
          px={{ base: "3", md: "4" }}
          py="3"
        >
          <Box minW="0" data-testid="declaration-question-copy">
            <Text fontWeight="bold" color="smText" overflowWrap="anywhere">
              {heading}
            </Text>
            {heading !== question && (
              <Text mt="0.5" fontSize="sm" color="smMuted" overflowWrap="anywhere">
                {question}
              </Text>
            )}
          </Box>
          <HStack
            data-testid="declaration-answer-status"
            spacing="3"
            justifySelf={{ base: "start", md: "end" }}
            flexShrink={0}
          >
            <StatusPill tone="neutral">{answerLabel(declaration.answer, t)}</StatusPill>
            <Icon as={isOpen ? FiChevronDown : FiChevronRight} color="smMuted" />
          </HStack>
        </Box>
      </Button>
      {isOpen && (
        <Stack
          id={panelId}
          role="region"
          aria-labelledby={buttonId}
          spacing="4"
          px={{ base: "3", md: "4" }}
          py="4"
          bg="smBackground"
          borderTop="1px solid"
          borderColor="smBorder"
        >
          {declaration.responseGroups.length === 0 ? (
            <MutedValue>{t("No answer details")}</MutedValue>
          ) : (
            declaration.responseGroups.map((group) => <RequirementGroup key={group.id} group={group} />)
          )}
        </Stack>
      )}
    </Box>
  );
};

const answerLabel = (answer: boolean | null | undefined, t: (value: string) => string) => {
  if (answer == null) return t("Not answered");
  return answer ? t("Yes") : t("No");
};
